import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom"; // Added useLocation
import { ToastContainer } from "react-toastify";
import {
  FiPlusCircle,
  FiLayers,
  FiPlus,
  FiBookOpen,
  FiEdit2,
  FiTrash2,
  FiCheckCircle,
  FiSave,
  FiX,
  FiChevronLeft,
  FiList,
} from "react-icons/fi";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import AddSectionForm from "./AddSectionForm";
import AddMoreQuestionsModal from "./AddMoreQuestionsModal";
import { FiFileText } from "react-icons/fi";

export default function QuestionList() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // --- STATE ---
  const [examName, setExamName] = useState(
    location.state?.paperName || "Loading...",
  );
  const [paperTotalQuestions, setPaperTotalQuestions] = useState(
    location.state?.totalQuestions || 0,
  ); // <--- Store Total Questions
  const [totalSectionsLimit, setTotalSectionsLimit] = useState(0);
  const [sections, setSections] = useState([]);
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  // --- MODAL STATES ---
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [editQuestionData, setEditQuestionData] = useState(null);

  const fetchExamDetails = useCallback(async () => {
    try {
      const res = await makeRequestAuth("post", "/admin/getExamById", {
        examId: id,
      });
      if (res?.data) {
        setExamName(res.data.paperName || res.data.testName || "Paper Details");
        setTotalSectionsLimit(res.data.totalSections || 0);
        // If API provides fresh totalQuestions, update it
        if (res.data.totalQuestions)
          setPaperTotalQuestions(res.data.totalQuestions);
      }
    } catch (error) {
      console.error("Failed to load exam details");
    }
  }, [id]);

  const fetchSections = useCallback(async () => {
    try {
      setLoading(true);
      const res = await makeRequestAuth("post", "/admin/getExamQuestions", {
        sourceId: id,
        pageNo: 1,
        size: 100,
      });
      if (!res.error) {
        const sorted =
          res.data?.list?.sort(
            (a, b) => (a.sectionOrder || 0) - (b.sectionOrder || 0),
          ) || [];
        setSections(sorted);
      }
    } catch (err) {
      showErrorToast("Failed to fetch sections");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      const activeSection = sections[activeSectionIdx];
      const res = await makeRequestAuth("post", "/admin/deleteQuestion", {
        sectionId: activeSection._id,
        questionId,
      });
      if (res?.error) return showErrorToast(res.message);
      showSuccessToast("Deleted");
      fetchSections();
    } catch {
      showErrorToast("Delete failed");
    }
  };

  const activeSection = sections[activeSectionIdx];

  return (
    <div className="p-4">
      <ToastContainer />
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-200 rounded-full text-gray-600 transition"
          >
            <FiChevronLeft size={24} />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 capitalize flex items-center gap-2">
              <FiList className="text-blue-600" /> {examName}
            </h2>
            <p className="text-gray-500 text-sm">
              Total Questions in Paper:{" "}
              <span className="font-bold">{paperTotalQuestions}</span>
            </p>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden min-h-[600px] flex flex-col md:flex-row">
        {/* LEFT SIDEBAR */}
        <div className="w-full md:w-72 border-r border-gray-100 bg-gray-50/50 p-4">
          <div className="flex items-center justify-between mb-4 px-2">
            <h3 className="font-bold text-gray-700 flex items-center gap-2 text-sm uppercase tracking-wide">
              <FiLayers className="text-blue-500" /> Sections
            </h3>
            <button
              onClick={() => {
                if (sections.length >= 3) showErrorToast(`Max ${3} sections.`);
                else setShowAddSectionModal(true);
              }}
              disabled={sections.length >= 3}
              className={`p-1.5 rounded-lg transition-colors ${sections.length >= 3 ? "bg-gray-200 text-gray-400" : "bg-blue-100 text-blue-600 hover:bg-blue-200"}`}
            >
              <FiPlus size={18} />
            </button>
          </div>
          <div className="space-y-2 overflow-y-auto max-h-[500px] custom-scrollbar">
            {loading ? (
              <div className="text-center py-4 text-xs text-gray-400">
                Loading...
              </div>
            ) : sections.length === 0 ? (
              <div className="text-center py-8 px-4 border-2 border-dashed rounded-xl">
                <p className="text-xs text-gray-400 mb-2">No sections.</p>
                <button
                  onClick={() => setShowAddSectionModal(true)}
                  className="text-blue-600 text-xs font-bold hover:underline"
                >
                  Create One
                </button>
              </div>
            ) : (
              sections.map((sec, idx) => (
                <button
                  key={sec._id}
                  onClick={() => setActiveSectionIdx(idx)}
                  className={`w-full text-left px-4 py-3 rounded-xl transition-all flex justify-between items-center group ${activeSectionIdx === idx ? "bg-white shadow-md border border-gray-100 text-blue-600 font-semibold ring-1 ring-blue-50" : "text-gray-500 hover:bg-gray-100"}`}
                >
                  <span className="truncate text-sm">{sec.sectionName}</span>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${activeSectionIdx === idx ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-600"}`}
                  >
                    {sec.questions?.length || 0}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* RIGHT CONTENT */}
        <div className="flex-1 p-6 bg-white relative">
          {activeSection ? (
            <>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-6 border-b border-gray-50">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {activeSection.sectionName}
                  </h2>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-gray-500 text-sm">
                      {activeSection.questions?.length} /{" "}
                      {activeSection.totalQuestions} Questions
                    </span>
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-600">
                      {activeSection.difficulty || "Medium"}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (
                      activeSection.questions.length >=
                      activeSection.totalQuestions
                    )
                      showErrorToast(`Section full.`);
                    else setShowAddQuestionModal(true);
                  }}
                  disabled={
                    activeSection.questions.length >=
                    activeSection.totalQuestions
                  }
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium shadow-sm bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-all"
                >
                  <FiPlus /> Create new question
                </button>
              </div>

              {activeSection?.paragraph?.isParagraph &&
                activeSection?.paragraph?.text && (
                  <div className="mb-6 p-6 rounded-2xl bg-blue-50 border border-blue-100">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-2">
                      Passage
                    </div>

                    <div
                      className="prose prose-sm max-w-none text-gray-800
          prose-img:rounded-xl prose-img:shadow-md
          prose-img:max-h-[400px] prose-img:object-contain"
                      dangerouslySetInnerHTML={{
                        __html: activeSection.paragraph.text,
                      }}
                    />
                  </div>
                )}

              <div className="space-y-4 overflow-y-auto max-h-[600px] custom-scrollbar pr-2">
                {activeSection.questions?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 border-2 border-dashed border-gray-100 rounded-2xl bg-gray-50/30">
                    <FiBookOpen size={40} className="text-gray-200 mb-3" />
                    <p className="text-gray-400 font-medium">
                      This section is empty.
                    </p>
                    <button
                      onClick={() => setShowAddQuestionModal(true)}
                      className="mt-4 text-emerald-600 font-bold text-sm hover:underline flex items-center gap-2"
                    >
                      <FiPlusCircle /> Create new question
                    </button>
                  </div>
                ) : (
                  activeSection.questions?.map((q, idx) => (
                    <QuestionRow
                      key={idx}
                      sectionId={activeSection?._id}
                      question={q}
                      onEdit={() => setEditQuestionData(q)}
                      onDelete={() => handleDeleteQuestion(q._id)}
                    />
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-300">
              <FiLayers size={64} className="mb-4 opacity-20" />
              <p className="font-medium">Select a section to view questions</p>
            </div>
          )}
        </div>
      </div>

      {/* MODALS */}
      {showAddSectionModal && (
        <AddSectionForm
          paperId={id}
          defaultQuestionCount={paperTotalQuestions} // <--- PASSING COUNT HERE
          onClose={() => setShowAddSectionModal(false)}
          onComplete={() => {
            setShowAddSectionModal(false);
            fetchSections();
          }}
        />
      )}
      {showAddQuestionModal && activeSection && (
        <AddMoreQuestionsModal
          sectionId={activeSection._id}
          nextQuestion={activeSection.questions?.length + 1}
          maxQuestions={activeSection.totalQuestions}
          onClose={() => setShowAddQuestionModal(false)}
          onSuccess={() => {
            setShowAddQuestionModal(false);
            fetchSections();
          }}
        />
      )}
      {editQuestionData && activeSection && (
        <EditQuestionModal
          sectionId={activeSection._id}
          question={editQuestionData}
          onClose={() => setEditQuestionData(null)}
          onSuccess={() => {
            setEditQuestionData(null);
            fetchSections();
          }}
        />
      )}
    </div>
  );
}

function QuestionRow({ question, sectionId, onEdit, onDelete }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [explanations, setExplanations] = useState([{ heading: "", text: "" }]);

  const addStep = () => {
    setExplanations([...explanations, { heading: "", text: "" }]);
  };

  const handleChange = (index, field, value) => {
    const updated = [...explanations];
    updated[index][field] = value;
    setExplanations(updated);
  };
  const saveExplanation = async () => {
    try {
      const payload = {
        sectionId: sectionId,
        questionId: question._id,
        explanation: explanations,
      };

      const res = await makeRequestAuth(
        "post",
        "/admin/addAnswersReason",
        payload,
      );

      if (!res.error) {
        showSuccessToast("Explanation saved successfully");
        setShowExplanationModal(false);
      }
    } catch (error) {
      console.error(error);
      showErrorToast(error?.response?.data?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    if (showExplanationModal) {
      if (question.explanation && question.explanation.length > 0) {
        setExplanations(question.explanation);
      } else {
        setExplanations([{ heading: "", text: "" }]);
      }
    }
  }, [showExplanationModal, question.explanation]);

  return (
    <div
      className={`border rounded-xl overflow-hidden transition-all duration-200 ${isExpanded ? "border-blue-200 shadow-md ring-1 ring-blue-50" : "border-gray-100 hover:border-blue-100"}`}
    >
      <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-bold">
        {question.questionType === "paragraph" ? "Paragraph" : "Text"}
      </span>

      <div
        className="flex items-center justify-between p-4 cursor-pointer bg-white"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-gray-50 text-gray-600 rounded-lg text-xs font-bold border border-gray-100">
            {question.questionNo}
          </span>
          <div
            className={`prose prose-sm max-w-none text-gray-700 ${
              isExpanded ? "" : "line-clamp-2"
            }`}
            dangerouslySetInnerHTML={{
              __html: question.questionText,
            }}
          />
        </div>
        <div className="flex items-center gap-2 pl-4">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowExplanationModal(true);
            }}
            className="p-2 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <FiFileText size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <FiEdit2 size={16} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className="px-14 pb-5 pt-1 bg-gray-50/50 border-t border-gray-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {Object.entries(question.options || {}).map(([key, text]) => (
              <div
                key={key}
                className={`flex items-center gap-3 p-2.5 rounded-lg border text-sm ${key === question.correctOption ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-white border-gray-200 text-gray-600"}`}
              >
                <span
                  className={`font-bold w-6 h-6 flex items-center justify-center rounded ${key === question.correctOption ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}
                >
                  {key}
                </span>
                {question.optionsType === "image" ? (
                  <img
                    src={text}
                    alt={`Option ${key}`}
                    className="max-h-[120px] rounded-lg border border-gray-200 object-contain"
                  />
                ) : (
                  <span className="font-medium">{text}</span>
                )}

                {key === question.correctOption && (
                  <FiCheckCircle className="ml-auto text-emerald-500" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showExplanationModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-lg rounded-2xl p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-4">
              Add Step-by-Step Explanation
            </h2>

            <div className="flex flex-col gap-4 max-h-[400px] overflow-y-auto">
              {explanations.map((step, index) => (
                <div key={index} className="border rounded-xl p-3 bg-gray-50">
                  <input
                    type="text"
                    placeholder="Heading (e.g., Step 1)"
                    value={step.heading}
                    onChange={(e) =>
                      handleChange(index, "heading", e.target.value)
                    }
                    className="w-full mb-2 px-3 py-2 border rounded-lg text-sm"
                  />
                  <textarea
                    placeholder="Explanation text..."
                    value={step.text}
                    onChange={(e) =>
                      handleChange(index, "text", e.target.value)
                    }
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-between mt-4">
              <button
                onClick={addStep}
                className="text-indigo-600 text-sm font-medium"
              >
                + Add Step
              </button>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowExplanationModal(false)}
                  className="px-4 py-2 text-sm bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>

                <button
                  onClick={saveExplanation}
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditQuestionModal({ sectionId, question, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    questionText: question.questionText || "",
    options: { ...question.options },
    correctOption: question.correctOption || "",
  });
  const handleUpdate = async () => {
    try {
      const res = await makeRequestAuth("post", "/admin/updateQuestion", {
        sectionId,
        questionNo: question.questionNo,
        questionText: formData.questionText,
        options: formData.options,
        correctOption: formData.correctOption,
      });
      if (res?.error) return showErrorToast(res.message);
      showSuccessToast("Updated");
      onSuccess();
    } catch {
      showErrorToast("Update failed");
    }
  };
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
      <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <h2 className="text-lg font-bold text-gray-800">
            Edit Question {question.questionNo}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full text-gray-500"
          >
            <FiX size={20} />
          </button>
        </div>
        <div className="p-6 space-y-6 overflow-y-auto custom-scrollbar">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase mb-2 block ml-1">
              Text
            </label>
            <textarea
              value={formData.questionText}
              onChange={(e) =>
                setFormData({ ...formData, questionText: e.target.value })
              }
              className="w-full bg-gray-50 rounded-xl px-4 py-3 focus:bg-white outline-none min-h-[100px] text-sm"
            />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <label className="text-xs font-bold text-gray-500 uppercase block ml-1">
              Options
            </label>
            {["A", "B", "C", "D"].map((key) => (
              <div key={key} className="flex gap-3 items-center">
                <button
                  onClick={() =>
                    setFormData({ ...formData, correctOption: key })
                  }
                  className={`w-10 h-10 rounded-lg font-bold border ${formData.correctOption === key ? "bg-emerald-500 text-white" : "bg-white text-gray-400"}`}
                >
                  {key}
                </button>
                <input
                  value={formData.options[key] || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      options: { ...formData.options, [key]: e.target.value },
                    })
                  }
                  className="flex-1 rounded-xl px-4 py-2.5 outline-none border bg-gray-50 focus:bg-white"
                />
              </div>
            ))}
          </div>
        </div>
        <div className="p-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 text-gray-600 font-bold hover:bg-gray-200 rounded-xl"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            className="flex-[2] bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold flex justify-center gap-2"
          >
            <FiSave /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
